import React from "react";
import { motion } from "framer-motion";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { useTenant } from "@/lib/TenantContext";

const Chapter = ({ chapter, index, total, chapterWord }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative grid grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 border-t border-eh-border first:border-t-0"
      data-testid={`story-chapter-${chapter.n}`}
    >
      <div className="col-span-12 md:col-span-3">
        <div className="flex items-baseline gap-3">
          <span className="font-script text-5xl text-eh-ochre-deep leading-none">{chapter.n}</span>
          <span className="eyebrow">{`${chapterWord} ${index + 1} / ${total}`}</span>
        </div>
      </div>
      <div className="col-span-12 md:col-span-9">
        <h3 className="font-display text-3xl md:text-5xl text-eh-ink leading-tight">{chapter.title}</h3>
        <p className="mt-4 text-lg md:text-xl text-eh-muted max-w-2xl leading-relaxed">{chapter.body}</p>
      </div>
    </motion.div>
  );
};

const Story = () => {
  // siteCopy = dictionary + Site Content overrides + the tenant record (lib/useSiteCopy.js).
  const siteCopy = useSiteCopy();
  const story = siteCopy.story;
  const mortar = siteCopy.images.mortar;
  const apothecary = siteCopy.images.apothecary;
  // The quote used to be signed "— Émilie" — the SEED tenant's first name, hardcoded, so
  // every practice's site attributed the quote to another person entirely (BUG-210). The
  // practice owner's own name lives on the tenant record (`tenant.owner_name`; same field
  // Footer.jsx / Hero.jsx read via `resolveBrandName`, though here it must be the OWNER's
  // personal name specifically, never the business/brand name a quote is not signed with).
  // A practice that has not set one gets no signature line at all, not a fallback name.
  const { tenant } = useTenant();
  const ownerName = tenant?.owner_name;
  return (
    <section
      id="story"
      data-testid="story-section"
      className="relative py-24 md:py-36 bg-eh-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <span className="eyebrow">{story.eyebrow}</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-eh-ink mt-4 leading-[1.05] tracking-[-0.015em] whitespace-pre-line">
              {story.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-lg md:text-xl text-eh-muted leading-relaxed">
              {story.lead}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            {story.chapters.map((c, i) => (
              <Chapter key={c.n} chapter={c} index={i} total={story.chapters.length} chapterWord={story.chapterWord} />
            ))}
          </div>

          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="overflow-hidden rounded-[2rem] aspect-[3/4] vignette"
              >
                <img src={mortar} alt="Mortar & pestle" className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.1 }}
                className="overflow-hidden rounded-[2rem] aspect-[4/3]"
              >
                <img src={apothecary} alt="Apothecary" className="w-full h-full object-cover" />
              </motion.div>
              <div className="rounded-[2rem] border border-eh-border bg-white/60 backdrop-blur p-6" data-testid="story-quote">
                {ownerName && (
                  <p className="font-script text-2xl text-eh-ochre-deep" data-testid="story-quote-signature">
                    — {ownerName}
                  </p>
                )}
                <p className="mt-2 text-eh-ink italic font-display text-xl leading-snug">
                  {story.quote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
